import { UseAppFormProps } from "hooks/useAppForm";
import { Component, FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import AdminEditor from "./AdminEditor";
import moment, { Moment } from "moment";

interface AppFormProps<T> {
    form: UseAppFormProps<T>
    onSubmit: (values: T) => void;
    children?: ReactNode;
    cols?: 2 | 3 | 4;
    isFormSearch?: boolean;
    shadow?: boolean;
    className?: string;
}
const Form = <T extends object>({
    form,
    onSubmit,
    children,
    cols,
    isFormSearch,
    shadow = true,
    ...props
}: AppFormProps<T>) => {

    const { validateForm, values } = form;
    const className = useMemo(() => {
        let cls = isFormSearch ? `${props.className} ` : `bg-white p-6 rounded ${props.className} `;
        if (shadow) {
            cls += " shadow "
        }
        switch (cols) {
            case 2:
                return cls + "grid grid-cols-1 md:grid-cols-2 gap-x-4 w-full ";
            case 3:
                return cls + "grid grid-cols-1 md:grid-cols-3 gap-x-4 w-full ";
            case 4:
                return cls + "grid grid-cols-1 md:grid-cols-4 gap-x-4 w-full ";
            default:
                return cls + "w-full";

        }
    }, [cols, isFormSearch, props.className])
    return <Fragment>
        <form
            ref={form.formRef}
            className={className}
            onSubmit={(e) => {
                e.preventDefault();
                if (validateForm()) {
                    onSubmit(values);
                }
            }}
        >
            {children}
        </form>
    </Fragment>
}
interface ActionProps {
    col?: number;
    children?: ReactNode;
}
const Action: FC<ActionProps> = ({ col, children }) => {
    const className = useMemo(() => {
        if (col) {
            return `flex justify-end space-x-2 md:col-span-${col} mt-2`;
        }
        return "flex justify-end space-x-2 ";
    }, [col])
    return <div className={className}>
        {children}
    </div>
}

interface ItemProps {
    col?: number;
    label?: ReactNode;
    required?: boolean;
    children?: ReactNode;
    error?: string;
    className?: string;
}
const Item: FC<ItemProps> = ({ col, label, required, children, error, ...props }) => {
    const className = useMemo(() => {
        if (col) {
            return `mb-2 md:col-span-${col} ${props.className}`;
        }
        return `mb-2  ${props.className}`;
    }, [col, props.className])
    return <div className={className}>
        {label && <label className="block text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>}
        {children}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
}

interface InputProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
}
const Input: FC<InputProps> = ({ value, onChange, col, error, label, placeholder, required }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-500`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error}>
        <input
            value={value || ""}
            onChange={e => onChange && onChange(e.currentTarget.value)}
            type="text"
            className={inputClassName}
            placeholder={placeholder}
        />
    </Item >
}
interface PasswordProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
}

const Password: FC<PasswordProps> = ({ value, onChange, col, error, label, placeholder, required }) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:outline-none focus:border-red-500 focus:ring-red-500 pr-10`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500 pr-10";
    }, [error]);

    return (
        <Item col={col} label={label} required={required} error={error}>
            <div className="relative">
                <input
                    value={value || ""}
                    onChange={e => onChange && onChange(e.currentTarget.value)}
                    type={showPassword ? "text" : "password"}
                    className={inputClassName}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                    {showPassword ? (
                        <i className="fas fa-eye-slash"></i>
                    ) : (
                        <i className="fas fa-eye"></i>
                    )}
                </button>
            </div>
        </Item>
    );
};


interface NumberProps extends ItemProps {
    value?: number;
    onChange?: (value?: number) => void;
    placeholder?: string;
}
const Number: FC<NumberProps> = ({ value, onChange, col, error, label, placeholder, required, }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error}>
        <input
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.currentTarget;
                onChange?.(value ? parseInt(value) : undefined);
            }}
            type="number"
            className={inputClassName}
            placeholder={placeholder}
        />
    </Item >
}

interface TextareaProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
    placeholder?: string;
    rows?: number;
}
const Textarea: FC<TextareaProps> = ({ value, onChange, col, error, label, placeholder, required, rows }) => {
    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded focus:outline-none focus:border-blue-500"
    }, [error])
    return <Item col={col} label={label} required={required} error={error}>
        <textarea
            value={value || ""}
            onChange={e => onChange && onChange(e.currentTarget.value)}
            className={inputClassName}
            placeholder={placeholder}
            rows={rows}
        />
    </Item >
}

interface EditorProps extends ItemProps {
    value?: string;
    onChange?: (value?: string) => void;
}
const Editor: FC<EditorProps> = ({ value, onChange, col, error, label, required }) => {
    return <Item col={col} label={label} required={required} error={error}>
        <AdminEditor
            value={value || ""}
            onChange={onChange}
        />
    </Item >
}
// AppForm

interface Option {
    label: string;
    value: string;
}

type SingleSelectProps = {
    multiple?: false;
    value?: string;
    onChange?: (value: string | "") => void;
};

type MultiSelectProps = {
    multiple: true;
    value?: string[];
    onChange?: (value: string[]) => void;
};

type BaseProps = {
    label?: ReactNode;
    required?: boolean;
    col?: number;
    placeholder?: string;
    options: Option[];
    error?: string;
};

type SelectProps = BaseProps & (SingleSelectProps | MultiSelectProps);

const Select: FC<SelectProps> = ({
    label,
    required,
    col,
    placeholder = "Chọn...",
    options,
    multiple = false,
    value,
    onChange,
    error
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [dropUp, setDropUp] = useState(false);
    const [ready, setReady] = useState(false); // ✅ trạng thái hiển thị sau khi check vị trí

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);

    const filteredOptions = useMemo(() => {
        return options.filter(opt =>
            opt.label.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, options]);

    const isSelected = (val: string) => {
        if (multiple && Array.isArray(value)) {
            return value.includes(val);
        }
        return value === val;
    };

    const handleSelect = (val: string) => {
        if (!multiple) {
            (onChange as (v: string | "") => void)?.(val);
            setOpen(false);
        } else {
            const current = Array.isArray(value) ? value : [];
            if (current.includes(val)) {
                (onChange as (v: string[]) => void)?.(
                    current.filter(v => v !== val)
                );
            } else {
                (onChange as (v: string[]) => void)?.([...current, val]);
            }
        }
    };

    const handleClear = () => {
        if (!multiple) {
            (onChange as (v: string | "") => void)?.("");
        } else {
            (onChange as (v: string[]) => void)?.([]);
        }
    };

    const wrapperClass = useMemo(() => {
        return col ? `mb-2 md:col-span-${col}` : "mb-2";
    }, [col]);

    // Đóng khi click ra ngoài
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (inputWrapperRef.current && !inputWrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Xác định mở lên hay xuống
    useEffect(() => {
        if (open && inputWrapperRef.current) {
            setReady(false); // Ẩn dropdown trước khi check
            const rect = inputWrapperRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;
            const dropdownHeight = 240; // max-h-60
            setDropUp(spaceBelow < dropdownHeight);

            // Sau 1 frame (16ms) mới bật hiển thị để tránh giật
            const t = setTimeout(() => setReady(true), 0);
            return () => clearTimeout(t);
        }
    }, [open]);

    return (
        <div className={wrapperClass} ref={wrapperRef}>
            {label && (
                <label className="block text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative" ref={inputWrapperRef}>
                <div
                    className={`w-full border px-4 py-2 rounded bg-white flex flex-wrap items-center gap-1 cursor-pointer group ${error ? "border-red-500" : ""
                        }`}
                    onClick={() => setOpen(o => !o)}
                >
                    {multiple ? (
                        Array.isArray(value) && value.length > 0 ? (
                            value.map(v => {
                                const label =
                                    options.find(opt => opt.value === v)?.label || v;
                                return (
                                    <span
                                        key={v}
                                        className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded flex items-center gap-1"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {label}
                                        <button
                                            type="button"
                                            className="p-2 flex items-center justify-center rounded-full text-red-500 hover:bg-red-200 transition-colors"
                                            onClick={() => handleSelect(v)}
                                        >
                                            <i className="fas fa-times w-5 h-5"></i>
                                        </button>
                                    </span>
                                );
                            })
                        ) : (
                            <span className="text-gray-400">{placeholder}</span>
                        )
                    ) : value ? (
                        <>
                            <span>
                                {options.find(opt => opt.value === value)?.label || value}
                            </span>
                            <button
                                type="button"
                                className="ml-auto p-3 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 transition-colors opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <i className="fas fa-times "></i>
                            </button>
                        </>
                    ) : (
                        <span className="text-gray-400">{placeholder}</span>
                    )}
                </div>

                {open && (
                    <div
                        className={`absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg transition-opacity duration-100 ${dropUp ? "bottom-full mb-1" : "top-full mt-1"
                            }`}
                        style={{
                            opacity: ready ? 1 : 0,
                            pointerEvents: ready ? "auto" : "none"
                        }}
                    >
                        <div className="p-2">
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Tìm kiếm..."
                                className="w-full border px-2 py-1 rounded"
                                onClick={e => e.stopPropagation()}
                            />
                        </div>
                        <ul className="max-h-60 overflow-auto">
                            {filteredOptions.map(opt => (
                                <li
                                    key={opt.value}
                                    onClick={() => handleSelect(opt.value)}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${isSelected(opt.value) ? "bg-blue-100" : ""
                                        }`}
                                >
                                    <span>{opt.label}</span>
                                    {isSelected(opt.value) && (
                                        <span className="text-blue-500">✓</span>
                                    )}
                                </li>
                            ))}
                            {filteredOptions.length === 0 && (
                                <li className="px-4 py-2 text-gray-400">
                                    Không có kết quả
                                </li>
                            )}
                        </ul>
                    </div>
                )}
            </div>

            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};
interface DatePickerProps extends ItemProps {
    value?: string | Date | Moment;
    onChange?: (value?: string) => void;
    placeholder?: string;
    format?: string;
}

export const DatePicker: FC<DatePickerProps> = ({
    value,
    onChange,
    col,
    error,
    label,
    placeholder,
    required,
    format = "DD/MM/YYYY HH:mm"
}) => {
    const [internalValue, setInternalValue] = useState<string>(
        value ? moment(value, format, true).format(format) : ""
    );

    const [viewDate, setViewDate] = useState<Moment>(
        moment(value, format, true).isValid()
            ? moment(value, format, true)
            : moment()
    );
    const [showCalendar, setShowCalendar] = useState(false);
    // const [viewDate, setViewDate] = useState<Moment>(
    //     moment(value, format, true).isValid()
    //         ? moment(value, format, true)
    //         : moment()
    // );
    const [hour, setHour] = useState<number>(
        moment(value).isValid() ? moment(value).hour() : 0
    );
    const [minute, setMinute] = useState<number>(
        moment(value).isValid() ? moment(value).minute() : 0
    );

    const calendarRef = useRef<HTMLDivElement>(null);

    const hasTime = useMemo(() => /H|h/.test(format), [format]);

    const inputClassName = useMemo(() => {
        if (Boolean(error)) {
            return `w-full border px-4 py-2 rounded border-red-500 focus:border-red-500 focus:ring-red-500 pr-8 focus:outline-none`;
        }
        return "w-full border px-4 py-2 rounded bg-white cursor-pointer pr-8 focus:outline-none focus:border-blue-500";
    }, [error]);

    // Đóng khi click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(e.target as Node)
            ) {
                setShowCalendar(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectDate = (day: Moment) => {
        let finalDate = day.clone();
        if (hasTime) {
            finalDate = finalDate.hour(hour).minute(minute);
        }
        const formatted = finalDate.format(format);
        setInternalValue(formatted);
        onChange && onChange(formatted);
        setViewDate(finalDate);
        setShowCalendar(false);
    };

    const clearDate = () => {
        setInternalValue("");
        setHour(0);
        setMinute(0);
        onChange && onChange(undefined);
    };

    const renderCalendar = () => {
        const startOfMonth = viewDate.clone().startOf("month").startOf("week");
        const endOfMonth = viewDate.clone().endOf("month").endOf("week");
        const days: Moment[] = [];
        let day = startOfMonth.clone();

        while (day.isBefore(endOfMonth)) {
            days.push(day.clone());
            day.add(1, "day");
        }

        const months = moment.months();
        const years = Array.from(
            { length: 101 },
            (_, i) => moment().year() - 50 + i
        );

        return (
            <div
                ref={calendarRef}
                className="absolute z-50 mt-1 bg-white border rounded shadow-lg p-3 w-80"
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().subtract(1, "month"))
                        }
                    >
                        ←
                    </button>
                    <div className="flex gap-2">
                        <select
                            value={viewDate.month()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().month(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {months.map((m, i) => (
                                <option value={i} key={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                        <select
                            value={viewDate.year()}
                            onChange={(e) =>
                                setViewDate(
                                    viewDate.clone().year(parseInt(e.target.value))
                                )
                            }
                            className="border rounded px-1"
                        >
                            {years.map((y) => (
                                <option value={y} key={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        className="px-2 py-1 hover:bg-gray-200 rounded"
                        onClick={() =>
                            setViewDate(viewDate.clone().add(1, "month"))
                        }
                    >
                        →
                    </button>
                </div>

                {/* Weekdays */}
                <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium mb-1">
                    {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                {/* Days */}
                <div className="grid grid-cols-7 gap-1 text-center">
                    {days.map((d, idx) => {
                        const isCurrentMonth = d.month() === viewDate.month();
                        const isSelected =
                            moment(internalValue, format, true).isValid() &&
                            d.isSame(
                                moment(internalValue, format, true),
                                "day"
                            );

                        return (
                            <button
                                key={idx}
                                onClick={() => selectDate(d)}
                                className={`p-1 rounded ${isSelected
                                    ? "bg-blue-500 text-white"
                                    : isCurrentMonth
                                        ? "hover:bg-gray-200"
                                        : "text-gray-400"
                                    }`}
                            >
                                {d.date()}
                            </button>
                        );
                    })}
                </div>

                {/* Time Picker (only if format has time) */}
                {hasTime && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                        <select
                            value={hour}
                            onChange={(e) => setHour(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                        :
                        <select
                            value={minute}
                            onChange={(e) => setMinute(parseInt(e.target.value))}
                            className="border rounded px-1 py-0.5"
                        >
                            {Array.from({ length: 60 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`col-span-${col || 1}`}>
            {label && (
                <label className="block mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <div className="relative group">
                <input
                    value={internalValue}
                    readOnly
                    className={inputClassName}
                    placeholder={placeholder || format}
                    onClick={() => setShowCalendar(true)}
                />
                {/* Nút clear */}
                {internalValue && (
                    <span
                        className="absolute w-6 h-6 mr-2 right-8 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={clearDate}
                    >
                        <i className="fas fa-times"></i>
                    </span>
                )}
                {/* Icon calendar */}
                <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                    onClick={() => setShowCalendar((prev) => !prev)}
                >
                    📅
                </span>
                {showCalendar && renderCalendar()}
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

interface CheckboxProps extends ItemProps {
    checked?: boolean;
    onChange?: (checked: boolean) => void;
}

const Checkbox: FC<CheckboxProps> = ({ checked, onChange, col, label, required, error }) => {
    const className = useMemo(() => {
        if (col) {
            return `mb-2 md:col-span-${col}`;
        }
        return "mb-2";
    }, [col]);

    return (
        <div className={className}>
            <label className="flex items-center space-x-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={checked || false}
                    onChange={e => onChange?.(e.target.checked)}
                    className={`w-4 h-4 border rounded cursor-pointer focus:ring-2 focus:ring-blue-500 ${error ? "border-red-500" : "border-gray-300"
                        }`}
                />
                <span className="text-gray-700">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </span>
            </label>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};


class AppForm<T extends object> extends Component<AppFormProps<T>> {
    static Item = Item;
    static Input = Input;
    static Password = Password;
    static Number = Number;
    static Editor = Editor;
    static Select = Select;
    static DatePicker = DatePicker;
    static Textarea = Textarea;
    static Checkbox = Checkbox;

    static Action = Action;
    render(): ReactNode {
        return <Form<T> {...this.props} />
    }
}
export default AppForm;

